import React from 'react';
import { NoteSearchResult } from '@teetsh/app/src/containers/Search/types';
import { format } from '@teetsh/app/src/utils/time/format';
import { DescriptionList } from '@teetsh/app/src/components/DescriptionList';
import HtmlViewer from '@teetsh/app/src/components/HtmlViewer';
import Line from '@teetsh/app/src/containers/Search/components/Line';
import { PaperClipIcon as PaperClip } from '@heroicons/react/24/outline';
import { getSeanceAttachmentUrl } from '@teetsh/app/src/utils/urls';
import tw from 'twin.macro';
import { DocumentArrowDownIcon as DocumentDownload } from '@heroicons/react/24/outline';
import isValid from '@teetsh/app/src/utils/time/isValid';
import {
  bgColors,
  getTextColorFromBackgroundColor,
} from '@teetsh/app/src/components/colors';
import { useUser } from '@teetsh/app/src/containers/App/context/useUser';
import { useTranslation } from 'react-i18next';

interface Props {
  note: NoteSearchResult;
}

export function NotePreview(props: Props) {
  const { note } = props;
  const { locale } = useUser();
  const { t } = useTranslation(['common']);
  return (
    <div tw="p-6">
      <div tw="text-xl font-bold mb-4">{note.title}</div>
      <div tw="space-y-4">
        <DescriptionList>
          {isValid(note.createdAt) && (
            <DescriptionList.Item label={t('common:search.results.createdAt')}>
              {format(note.createdAt, locale, 'eeee dd MMMM yyyy à hh:mm')}
            </DescriptionList.Item>
          )}
        </DescriptionList>
        <div tw="grid grid-cols-12 gap-x-2 gap-y-4">
          {(note?.attachments || []).length > 0 && (
            <Line icon={PaperClip}>
              <div tw="space-x-2">
                {note.attachments.map(a => (
                  <a
                    onClick={e => e.stopPropagation()}
                    href={getSeanceAttachmentUrl(
                      a.s3Path,
                      a.s3Filename,
                      a.label,
                    )}
                    rel="noreferrer noopener"
                    css={[
                      tw`text-neutral-moderate hover:underline break-all font-medium`,
                    ]}
                    target="_blank"
                  >
                    <div
                      key={a.label}
                      tw="rounded-xl bg-neutral-quiet hover:bg-neutral-moderate p-2 py-1 inline-flex text-xs items-center cursor-pointer"
                    >
                      <DocumentDownload tw="h-4 w-4 mr-1 text-neutral-moderate" />
                      {a.label}
                    </div>
                  </a>
                ))}
              </div>
            </Line>
          )}
        </div>
        <div
          role="presentation"
          css={[
            tw`py-3 px-4 rounded-lg shadow-md`,
            bgColors(note.color || 'white'),
            getTextColorFromBackgroundColor(note.color || 'white'),
          ]}
        >
          <HtmlViewer onChange={() => {}} text={note.description} />
        </div>
      </div>
    </div>
  );
}
